/* eslint-disable import/no-cycle */

import { call, put, select, takeLatest, takeLeading } from 'redux-saga/effects';
import { toast } from 'react-toastify';

import {
  almaOnboardingApi,
  emailVerifyApi,
  forgotPasswordApi,
  getUserRoleApi,
  loginApi,
  logoutApi,
  requestMeForRole,
  resolveAuthRoleFromLoginUser,
  parseMeResponsePayload,
  registerApi,
  resendOTPApi,
  resetPasswordApi,
  selectBusinessCategories,
  selectLanguageApi,
  selectPaymentGateWayMethodsApi,
  updatePaymentMethodApi,
  // selectPaymentStatus,
} from '../requests/authenticationRequests';

import * as types from '../../authentication/actionTypes';
import { startLoader, stopLoader } from '../../../utility/commonFunction';
import {
  forgotPasswordError,
  forgotPasswordSuccess,
  loginErr,
  loginSuccess,
  logoutSuccess,
  registerError,
  registerSuccess,
  resetPasswordError,
  resetPasswordSuccess,
  setAlmaOnboardingDetails,
  setAlmaOnboardingStatus,
  setBusiessCategories,
  setLanguage,
  setMe,
  setPaymentTypes,
  // setPaymentStatus,
  setUser,
  startLogOut,
  verifyEmailStatus,
  getMe,
  getMeFailure,
} from '../../authentication/actions';
import { removeItem, setItem, setLang } from '../../../utility/localStorageControl';

import { getPersistor } from '../../persistor';

function* loginHandler(values) {
  // yield put(loginBegin());
  // yield startLoader('transparent');

  // eslint-disable-next-line no-debugger
  // debugger;
  try {
    const { data } = yield call(loginApi, values?.payload?.data);

    if (data?.status) {
      yield setItem('access_token', data?.data?.access_token);
      yield toast.success(data?.message);
      yield put(loginSuccess(data.data.access_token));
      const modifydata = {
        business_name: data?.data?.user?.user_business_details?.business_name,
        logo: data?.data?.user?.user_business_details?.logo,
        colour: data?.data?.user?.user_business_details?.colour,
        status: data?.data?.user?.user_business_details?.status,
        status_label: data?.data?.user?.user_business_details?.status_label,
        email: data?.data?.user?.email,
        role: resolveAuthRoleFromLoginUser(data?.data?.user),
        slug: data?.data?.user?.user_business_details?.link?.[0]?.slug,
        business_category: data?.data?.user?.user_business_details?.business_category?.name,
        business_category_id: data?.data?.user?.user_business_details?.business_category?.id,
        is_onboarding_completed: data?.data?.onboarding_steps?.all_completed,
        current_step: data?.data?.onboarding_steps?.current_step,
        onboarding_steps: data?.data?.onboarding_steps,
      };

      yield put(setUser(modifydata));
      yield put(setAlmaOnboardingStatus(data?.data?.user?.user_business_details?.alma_onboarding_status));
      const payload = {
        data: data?.data?.user?.user_business_details?.alma_onboarding_details,
        label_status: data?.data?.user?.user_business_details?.alma_onboarding_status_label,
      };
      yield put(setAlmaOnboardingDetails(payload));
      // yield put(setUser(data?.data?.user));

      // Fetch full profile and store in auth.me — use Bearer from login response (same as reset-password) so cookie/interceptor cannot lag.
      try {
        const accessToken = data?.data?.access_token;
        if (accessToken) {
          const userRole = resolveAuthRoleFromLoginUser(data?.data?.user);
          const meRes = yield call(requestMeForRole, userRole, accessToken);
          const meData = parseMeResponsePayload(meRes);
          if (meData) {
            yield put(setMe(meData));
          }
        }
      } catch (meErr) {
        // Non-blocking: keep login success even if me endpoint fails
        console.warn('me profile fetch failed after login', meErr);
      }

      // Do not use window.location here — full reload unloads the page before DevTools can show login/me
      // response bodies, and is redundant: RootRedirect at "/" sends merchants to onboarding (same path logic).
    } else if (data?.errors?.code === 'ERR068') {
      yield toast.error(data?.errors?.message);
      yield put(loginErr({ status: false, email: values?.payload?.data?.email }));
    } else {
      yield toast.error(data?.errors?.message);
      yield put(loginErr({ status: false, email: '' }));
    }
  } catch (err) {
    yield put(loginErr(err));
  }
}

function* registerHandler(values) {
  // yield startLoader('transparent');
  try {
    const { data } = yield call(registerApi, values?.payload);


    if (data?.status) {
      const payload = {
        data: data?.data?.user?.user_business_details?.alma_onboarding_details,
        label_status: data?.data?.user?.user_business_details?.alma_onboarding_status_label,
      };
      yield put(setAlmaOnboardingStatus(data?.data?.user?.user_business_details?.alma_onboarding_status));
      yield put(setAlmaOnboardingDetails(payload));
      yield toast.success(data?.message);
      yield put(registerSuccess(data?.data));
    } else {
      yield toast.error(data?.errors?.message);
      yield put(registerError(data?.errors?.form_errors));
    }
  } catch (err) {
    yield put(loginErr(err));
  }
}

function* getUserRoleHandler() {
  try {
    const { data } = yield call(getUserRoleApi);
    if (data?.data?.user) {
      yield setItem('users_role', data?.data?.role);
      yield setItem('users_permissions', data?.data?.permissions);
    } else {
      yield toast.error('Unauthorized user does not have login access. Please contact the admin for assistance.');
      yield put(startLogOut());
    }
  } finally {
    yield stopLoader('transparent');
  }
}

function* forgotPasswordHandler(values) {
  try {
    const { data } = yield call(forgotPasswordApi, values.payload);
    if (data.status) {
      const filterData = {
        email: data?.data?.email,
        redirect: data?.status || true,
        verify_token: data?.data?.verify_token,
      };
      yield toast.success(data?.message);
      yield put(forgotPasswordSuccess(filterData));
    } else {
      yield toast.error(data?.errors?.message);
      yield put(forgotPasswordError(data.errors));
    }
  } catch (err) {
    console.log(err);
  }
}

function* resetPasswordHandler(values) {
  try {
    const payload = values.payload || {};
    const useForgotStage3 = payload.useForgotPasswordStage3 === true;
    const apiPayload = useForgotStage3
      ? {
          stage: '3',
          email: payload.email,
          verify_token: payload.verify_token,
          password: payload.password,
          password_confirmation: payload.password_confirmation,
        }
      : {
          token: payload.token,
          email: payload.email,
          password: payload.password,
          password_confirmation: payload.password_confirmation,
        };
    const apiFn = useForgotStage3 ? forgotPasswordApi : resetPasswordApi;
    const { data } = yield call(apiFn, apiPayload);
    if (data?.status) {
      yield toast.success(data?.message);
      const resetData = data?.data && typeof data.data === 'object' ? data.data : {};
      const accessToken = resetData.access_token ?? resetData.token ?? data?.access_token;
      const resetUser = resetData.user;
      const onboardingSteps =
        resetData?.onboarding_steps ?? resetData?.user?.onboarding_steps ?? null;
      // If API returns user + access_token, log the user in immediately (same as sign-in)
      if (accessToken && resetUser) {
        yield setItem('access_token', accessToken);
        yield put(loginSuccess(accessToken));
        const modifydata = {
          business_name: resetUser?.user_business_details?.business_name,
          logo: resetUser?.user_business_details?.logo,
          colour: resetUser?.user_business_details?.colour,
          status: resetUser?.user_business_details?.status,
          status_label: resetUser?.user_business_details?.status_label,
          email: resetUser?.email,
          role: resolveAuthRoleFromLoginUser(resetUser),
          slug: resetUser?.user_business_details?.link?.[0]?.slug,
          business_category: resetUser?.user_business_details?.business_category?.name,
          business_category_id: resetUser?.user_business_details?.business_category?.id,
          is_onboarding_completed: onboardingSteps?.all_completed,
          current_step: onboardingSteps?.current_step,
          onboarding_steps: onboardingSteps,
        };
        yield put(setUser(modifydata));
        yield put(setAlmaOnboardingStatus(resetUser?.user_business_details?.alma_onboarding_status));
        const almaPayload = {
          data: resetUser?.user_business_details?.alma_onboarding_details,
          label_status: resetUser?.user_business_details?.alma_onboarding_status_label,
        };
        yield put(setAlmaOnboardingDetails(almaPayload));
      }

      yield put(resetPasswordSuccess(resetData));

      if (accessToken && resetUser) {
        const persistor = getPersistor();
        if (persistor && typeof persistor.flush === 'function') {
          yield call([persistor, persistor.flush]);
        }
        // GET_ME with Bearer from this response; takeLeading(GET_ME) avoids mount-time getMe() cancelling this call.
        if (resolveAuthRoleFromLoginUser(resetUser)) {
          yield put(getMe({ accessToken }));
        }
        // Navigation after reset is handled by PostResetPasswordNavigate (SPA).
      }
    } else {
      yield toast.error(data?.message || 'Failed to reset password');
      yield put(resetPasswordError(data?.errors || {}));
    }
  } catch (err) {
    yield toast.error(err?.response?.data?.errors?.message || 'Failed to reset password');
    yield put(resetPasswordError(err?.response?.data?.errors || {}));
  }
}

function* resendOTPHandler(values) {
  try {
    const { data } = yield call(resendOTPApi, values?.data);
    if (data.status) {
      yield toast.success(data?.message);
    } else {
      yield toast.error(data?.errors?.form_errors?.email);
    }
  } catch (err) {
    console.log(err);
  }
}

function* emailVerifiedHandler(values) {
  try {
    const { data } = yield call(emailVerifyApi, values?.data);
    if (data.status) {
      yield put(verifyEmailStatus(data?.data));
      yield toast.success(data?.message);
      yield put(loginSuccess(data?.data?.access_token));
      yield setItem('access_token', data?.data?.access_token);
      const modifydata = {
        business_name: data?.data?.user?.user_business_details?.business_name,
        logo: data?.data?.user?.user_business_details?.logo,
        colour: data?.data?.user?.user_business_details?.colour,
        status: data?.data?.user?.user_business_details?.status,
        email: data?.data?.user?.email,
        role: resolveAuthRoleFromLoginUser(data?.data?.user),
        slug: data?.data?.user?.user_business_details?.link?.[0]?.slug,
      };
      yield put(setUser(modifydata));
      try {
        const accessToken = data?.data?.access_token;
        if (accessToken) {
          const userRole = resolveAuthRoleFromLoginUser(data?.data?.user);
          const meRes = yield call(requestMeForRole, userRole, accessToken);
          const meData = parseMeResponsePayload(meRes);
          if (meData) yield put(setMe(meData));
        }
      } catch (meErr) {
        console.warn('me profile fetch failed after email verify', meErr);
      }
      window.location.href = '/profile';
    } else {
      yield toast.error(data?.errors?.message);
      yield put(verifyEmailStatus({ status: false, email: values?.data?.email }));
    }
  } catch (err) {
    console.log(err);
  }
}

function* selectPaymemtGateWayHandler(values) {
  yield startLoader('transparent');
  try {
    const { data } = yield call(selectPaymentGateWayMethodsApi, values?.data);
    if (data.status) {
      yield put(setPaymentTypes(data?.data));
      yield stopLoader('transparent');
    } else {
      yield toast.error(data?.errors?.message);
      yield stopLoader('transparent');
    }
  } catch (err) {
    console.log(err);
  }
}
function* selectLanguageHandler(values) {
  yield startLoader('transparent');
  try {
    const { data } = yield call(selectLanguageApi, values?.data);
    if (data.status) {
      yield put(setLanguage(data?.data));
      yield stopLoader('transparent');
      localStorage.setItem('setLang', data?.lang);
      yield setLang('setLang', data?.lang);
    } else {
      yield toast.error(data?.errors?.message);
      yield stopLoader('transparent');
    }
  } catch (err) {
    yield stopLoader('transparent');
  }
}
function* selectBusinessCategoriesHandler(values) {
  yield startLoader('transparent');
  try {
    const { data } = yield call(selectBusinessCategories, values?.data);
    if (data.status) {
      yield put(setBusiessCategories(data?.data));
      yield stopLoader('transparent');
    } else {
      yield toast.error(data?.errors?.message);
      yield stopLoader('transparent');
    }
  } catch (err) {
    console.log(err);
    yield stopLoader('transparent');
  }
}

function* logOutHandler() {
  try {
    yield startLoader('transparent');
    const { data } = yield call(logoutApi);
    if (data) {
      yield stopLoader('transparent');
      yield localStorage.clear();
      yield removeItem('access_token');
      yield removeItem('logedIn');
      yield put(logoutSuccess(false));
      yield toast.success(data?.message);
    }
  } finally {
    yield stopLoader('transparent');
  }
}
function* submitAlmaOnboardingDetails(value) {
  yield startLoader('transparent');
  try {
    const { data } = yield call(almaOnboardingApi, value?.payload);

    if (data.status) {
      yield stopLoader('transparent');
      yield put(setAlmaOnboardingStatus(data?.data?.alma_onboarding_status));
      // yield put(setAlmaOnboardingDetails(data?.data?.alma_account_details));
      const payload = {
        data: data?.data?.alma_account_details,
        label_status: data?.data?.alma_account_details?.alma_onboarding_status_label,
      };
      yield put(setAlmaOnboardingDetails(payload));
      yield toast.success(data?.message);
    } else {
      yield toast.error(data?.errors?.message);
      yield stopLoader('transparent');
    }
  } catch (err) {
    yield stopLoader('transparent');
    console.log(err);
  }
}
function* submitUpdatePaymentMethod(value) {
  yield startLoader('transparent');
  try {
    const { data } = yield call(updatePaymentMethodApi, value?.payload);

    if (data.status) {
      yield stopLoader('transparent');
      // yield put(setAlmaOnboardingStatus(data?.data?.alma_onboarding_status));
      // yield put(setAlmaOnboardingDetails(data?.data?.alma_account_details));

      yield toast.success(data?.message);
    } else {
      yield toast.error(data?.errors?.message);
      yield stopLoader('transparent');
    }
  } catch (err) {
    yield stopLoader('transparent');
    console.log(err);
  }
}

function* getMeHandler(action) {
  try {
    const accessTokenOverride = action?.payload?.accessToken;
    const user = yield select((state) => state?.auth?.user);
    const userRole = resolveAuthRoleFromLoginUser(user);
    const meRes = yield call(requestMeForRole, userRole, accessTokenOverride);
    const meData = parseMeResponsePayload(meRes);
    if (meData) {
      yield put(setMe(meData));
    } else {
      yield put(getMeFailure());
    }
  } catch (err) {
    console.warn('me profile fetch failed', err);
    yield put(getMeFailure());
  }
}

export default function* authenticationSaga() {
  yield takeLatest(types.START_LOGIN, loginHandler);
  // takeLeading: a second getMe() (e.g. profile mount) must not cancel the first — that dropped Bearer from reset/login.
  yield takeLeading(types.GET_ME, getMeHandler);
  yield takeLatest(types.REGISTER_USER, registerHandler);
  yield takeLatest(types.RESEND_OTP, resendOTPHandler);
  yield takeLatest(types.EMAIL_VERIFIED, emailVerifiedHandler);
  yield takeLatest(types.GET_USER_ROLE, getUserRoleHandler);
  yield takeLatest(types.START_LOGOUT, logOutHandler);
  yield takeLatest(types?.FORGOT_PASSWORD, forgotPasswordHandler);
  yield takeLatest(types.RESET_PASSWORD, resetPasswordHandler);
  yield takeLatest(types.SELECT_LANGUAGE, selectLanguageHandler);
  yield takeLatest(types.SELECT_LANGUAGE, selectBusinessCategoriesHandler);
  yield takeLatest(types.GET_BUSINESS_CATEGORIES, selectBusinessCategoriesHandler);
  yield takeLatest(types.SELECT_LANGUAGE, selectPaymemtGateWayHandler);
  yield takeLatest(types.ALMA_ONBOARDING_DETAILS, submitAlmaOnboardingDetails);
  yield takeLatest(types.UPDATE_PAYMENT_METHOD, submitUpdatePaymentMethod);
}
